/**
 * lib/pdfIcons.ts
 * Vector glyphs drawn with jsPDF primitives, echoing the app's nav
 * icons: FileText (invoice), FileSignature (quote), Receipt (payment).
 * Used small + white in the banner, and large + faint as a page
 * watermark. Stroke colour/width are set from the size and colour.
 */
import type { jsPDF } from 'jspdf';

export type DocKind = 'invoice' | 'quote' | 'receipt';

/** Short brand wordmark — strips a trailing "Proprietary Limited" / "(Pty) Ltd". */
export function brandName(c: { tradingName?: string; legalName: string }): string {
  const src = c.tradingName || c.legalName || 'Acepsle';
  return src.replace(/\s*(proprietary\s+limited|\(?pty\)?\.?\s*ltd\.?)\s*$/i, '').trim() || src;
}

function poly(doc: jsPDF, pts: Array<[number, number]>) {
  for (let i = 0; i < pts.length - 1; i++) {
    doc.line(pts[i][0], pts[i][1], pts[i + 1][0], pts[i + 1][1]);
  }
}

/** Draw the doc-type glyph centred at (cx, cy), overall height `s`. */
export function drawDocGlyph(
  doc: jsPDF,
  kind: DocKind,
  cx: number,
  cy: number,
  s: number,
  color: [number, number, number],
) {
  const w = s * 0.72;
  const h = s;
  const x = cx - w / 2;
  const y = cy - h / 2;
  doc.setDrawColor(color[0], color[1], color[2]);
  doc.setLineWidth(Math.max(0.3, s * 0.05));
  doc.setLineCap('round');
  doc.setLineJoin('round');

  if (kind === 'receipt') {
    const zig = s * 0.1;
    const left = x;
    const rightX = x + w;
    const top = y;
    const bot = y + h - zig;
    const pts: Array<[number, number]> = [
      [left, bot + zig],
      [left, top],
      [rightX, top],
      [rightX, bot + zig],
    ];
    poly(doc, pts);
    // zigzag bottom edge
    const teeth = 4;
    const step = w / teeth;
    const zz: Array<[number, number]> = [[rightX, bot + zig]];
    for (let i = 0; i < teeth; i++) {
      zz.push([rightX - step * (i + 0.5), bot]);
      zz.push([rightX - step * (i + 1), bot + zig]);
    }
    poly(doc, zz);
    // content lines
    doc.line(x + w * 0.2, y + h * 0.3, x + w * 0.8, y + h * 0.3);
    doc.line(x + w * 0.2, y + h * 0.46, x + w * 0.8, y + h * 0.46);
    doc.line(x + w * 0.2, y + h * 0.62, x + w * 0.6, y + h * 0.62);
    return;
  }

  // Page outline with a folded top-right corner (shared by invoice/quote)
  const fold = s * 0.22;
  poly(doc, [
    [x, y],
    [x + w - fold, y],
    [x + w, y + fold],
    [x + w, y + h],
    [x, y + h],
    [x, y],
  ]);
  doc.line(x + w - fold, y, x + w - fold, y + fold);
  doc.line(x + w - fold, y + fold, x + w, y + fold);

  if (kind === 'invoice') {
    doc.line(x + w * 0.2, y + h * 0.46, x + w * 0.8, y + h * 0.46);
    doc.line(x + w * 0.2, y + h * 0.6, x + w * 0.8, y + h * 0.6);
    doc.line(x + w * 0.2, y + h * 0.74, x + w * 0.55, y + h * 0.74);
  } else {
    // quote = FileSignature: a couple of lines + a signature squiggle + pen stroke
    doc.line(x + w * 0.2, y + h * 0.38, x + w * 0.8, y + h * 0.38);
    const sy = y + h * 0.64;
    poly(doc, [
      [x + w * 0.18, sy],
      [x + w * 0.32, sy - s * 0.07],
      [x + w * 0.46, sy + s * 0.05],
      [x + w * 0.6, sy - s * 0.06],
      [x + w * 0.72, sy + s * 0.02],
    ]);
    doc.line(x + w * 0.55, y + h * 0.98, x + w * 0.98, y + h * 0.56);
  }
}

/** Draw a large faint centred watermark of the glyph. */
export function drawWatermark(doc: jsPDF, kind: DocKind, color: [number, number, number] = [255, 107, 53]) {
  type GStateOpts = { opacity?: number; 'stroke-opacity'?: number };
  type WithGState = jsPDF & { GState: new (o: GStateOpts) => unknown; setGState: (g: unknown) => void };
  const d = doc as WithGState;
  d.setGState(new d.GState({ opacity: 0.1, 'stroke-opacity': 0.1 }));
  drawDocGlyph(doc, kind, 105, 202, 55, color);
  d.setGState(new d.GState({ opacity: 1, 'stroke-opacity': 1 }));
  doc.setLineWidth(0.2); // restore hairline so table/section rules aren't left thick
}
